import { Delete as DeleteIcon, Description as DocIcon, Download as DownloadIcon, PictureAsPdf as PdfIcon } from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, Grid, IconButton, Paper, Stack, Tooltip, Typography, styled } from "@mui/material";
import React, { useRef, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { VForm, VTextField, useVForm } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { globalBackground, globalColorText } from "../../../../shared/utils";
import * as translateUtils from "../../../../shared/utils/globalTranslateSignature";
import { DisabledOverlay } from "../../components/DisabledOverlay";
import { getFileTypeFromName, useEnvelopeDocuments } from "../../hooks/useEnvelopeDocuments";
import { IDocument } from "../../types/types";

interface Props {
	active: boolean;
	envelopeId?: string;
	documents: IDocument[];
	onChange: (nextDocuments: IDocument[]) => void;
	envelopeStatus?: string;
}

const translateStatus = (s: string) =>
	typeof translateUtils.globalTranslateSignatureDocumentStatus === "function"
		? translateUtils.globalTranslateSignatureDocumentStatus(s)
		: s;

const DocumentCard = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(2),
	display: "flex",
	alignItems: "center",
	gap: theme.spacing(2),
	borderRadius: 12,
	border: `1px solid ${theme.palette.divider}`,
	transition: "background-color 0.2s",
	"&:hover": {
		backgroundColor: theme.palette.action.hover,
	},
}));

export default function EnvelopeTabDocumentos({ active, envelopeId, documents, onChange, envelopeStatus }: Props) {
	const toast = useToast();
	const { formRef } = useVForm();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const { isSubmitting, canAddDocument, handleAdd, handleDownload, handleRemove } = useEnvelopeDocuments({
		envelopeId,
		documents,
		onChange,
		envelopeStatus,
	});

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0] || null;
		setSelectedFile(file);
		if (file) {
			formRef.current?.setFieldValue("filename", file.name.replace(/\.[^.]+$/, ""));
		}
	};

	const handleFormSubmit = async () => {
		if (!selectedFile) {
			toast.present({ type: "error", messageOrContent: <ToastContent title="Erro" content="Selecione um arquivo para adicionar." /> });
			return;
		}
		const values = formRef.current?.getData() || {};
		await handleAdd(selectedFile, values.filename);
		setSelectedFile(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	if (!envelopeId) return <DisabledOverlay />;

	return (
		<Stack spacing={2}>
			{/* Form de upload */}
			<Box component={Paper} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Adicionar documento
				</Typography>
				<VForm
					ref={formRef}
					onSubmit={handleFormSubmit}
					onPointerEnterCapture={undefined as any}
					onPointerLeaveCapture={undefined as any}
					placeholder={undefined as any}
				>
					<Grid container spacing={2} alignItems="flex-end">
						<Grid item xs={12} sm={6}>
							<VTextField name="filename" label="Nome do documento" fullWidth disabled={!canAddDocument} />
						</Grid>
						<Grid item xs={12} sm={4}>
							<Button variant="outlined" component="label" disabled={!canAddDocument}>
								{selectedFile ? selectedFile.name : "Selecionar Arquivo"}
								<input ref={fileInputRef} type="file" hidden onChange={handleFileChange} accept=".pdf,.docx" />
							</Button>
						</Grid>
						<Grid item xs={2} sm="auto" sx={{ ml: "auto" }}>
							<Button type="submit" variant="contained" disabled={!selectedFile || isSubmitting || !canAddDocument}>
								{isSubmitting ? (
									<>
										<CircularProgress size={16} sx={{ mr: 1 }} /> Adicionando…
									</>
								) : (
									"Adicionar"
								)}
							</Button>
						</Grid>
					</Grid>
				</VForm>
			</Box>

			{/* Lista com o novo design */}
			<Box component={Paper} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Documentos ({documents.length})
				</Typography>
				<Grid container spacing={1}>
					{documents.length === 0 && (
						<Grid item xs={12}>
							<Typography variant="body2" color="text.secondary">
								Nenhum documento ainda.
							</Typography>
						</Grid>
					)}
					{documents.map((d) => {
						const labelStatus = translateStatus(d.status);
						const fileIcon = getFileTypeFromName(d.originalName || "") === ".pdf" ? <PdfIcon color="primary" /> : <DocIcon color="primary" />;

						return (
							<Grid key={d.id} item xs={12} md={6}>
								<DocumentCard>
									{fileIcon}
									<Box sx={{ flexGrow: 1, overflow: "hidden" }}>
										<Tooltip title={d.filename}>
											<span>
												{/* ✅ NOVO: Tooltip para mostrar o nome completo */}
												<Typography fontWeight={600} noWrap sx={{ textOverflow: "ellipsis", overflow: "hidden" }}>
													{/* ✅ NOVO: Estilo para evitar quebra de linha */}
													{d.filename}
												</Typography>
											</span>
										</Tooltip>

										<Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
											<Chip
												size="small"
												label={labelStatus}
												sx={{
													color: globalColorText?.(d.status) ?? "inherit",
													background: globalBackground?.(d.status) ?? "transparent",
													border: "0.5px solid #f1f1f1",
													height: 22,
												}}
											/>
										</Stack>
									</Box>
									<Stack direction="row" spacing={1}>
										<IconButton onClick={() => handleDownload(d.id)} aria-label="baixar documento">
											<DownloadIcon />
										</IconButton>
										{canAddDocument && (
											<IconButton onClick={() => handleRemove(d.id)} aria-label="remover documento">
												<DeleteIcon color="error" />
											</IconButton>
										)}
									</Stack>
								</DocumentCard>
							</Grid>
						);
					})}
				</Grid>
			</Box>
		</Stack>
	);
}
